import React, { useEffect } from "react";
import CheckRound from "assets/icons/check-round.svg";
import { cn } from "helpers/style";
import styles from "./TextField.module.scss";

export type Props = Partial<
  {
    value: string | number;
    type: string;
    label: React.ReactChild;
    placeholder: string;
    disabled: boolean;
    //todo :  убрать boolean из error
    error?: React.ReactChild | boolean;
    prefix: React.ReactChild;
    postfix: React.ReactChild;
    onFocus: (e: React.FocusEvent) => void;
    onBlur: (e: React.FocusEvent) => void;
    success: boolean;
    autoFocus: boolean;
    onEnter: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    backgroundText?: string;
    size?: number;
    className?: string;
    classNameWrap?: string;
    /* HTMLAttributes<HTMLInputElement> for some reason not provide autoComplete from HTMLInputElement */
    autoComplete?: string;
    largeFromTablet?: boolean;
    classNamePrefix?: string;
    classNamePostfix?: string;
  } & Omit<React.HTMLAttributes<HTMLInputElement>, "prefix">
>;

const TextField = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      type = "text",
      prefix,
      postfix,
      onFocus,
      onBlur,
      onKeyPress,
      onEnter,
      success,
      backgroundText,
      value,
      error,
      className,
      classNameWrap,
      largeFromTablet,
      classNamePrefix,
      classNamePostfix,
      ...props
    },
    ref: React.MutableRefObject<HTMLInputElement>
  ) => {
    const { label, placeholder, disabled, autoFocus } = props;
    useEffect(() => {
      if (autoFocus && ref?.current) {
        //set to last letter after focus
        ref.current.focus();
        ref.current.selectionStart = ref.current.value.length;
        ref.current.selectionEnd = ref.current.value.length;
      }
    }, [autoFocus, ref]);
    function handleFocus(e: React.FocusEvent) {
      onFocus && onFocus(e);
    }
    function handleBlur(e: React.FocusEvent) {
      onBlur && onBlur(e);
    }
    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key === "Enter" && onEnter) {
        onEnter(e);
      }
    }

    return (
      <div
        className={cn([
          "text-field",
          styles.textField,
          largeFromTablet && styles.textFieldLargeFromTablet,
          className,
        ])}
      >
        {label && <span className={styles.textFieldLabel}>{label}</span>}

        <div
          className={cn([
            "text-field-wrap",
            styles.textFieldWrap,
            disabled && styles.textFieldWrapDisabled,
            success && styles.textFieldWrapSuccess,
            error && styles.textFieldWrapError,
            classNameWrap,
          ])}
        >
          {backgroundText && value && (
            <span className={styles.textFieldBackgroundText}>
              <span>{value}</span>
              &nbsp;&nbsp;
              <span>{backgroundText}</span>
            </span>
          )}

          {prefix && (
            <div className={cn([styles.textFieldPrefix, classNamePrefix])}>
              {prefix}
            </div>
          )}

          <input
            ref={ref}
            type={type}
            className={styles.textFieldInput}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onKeyPress={onKeyPress}
            value={value}
            placeholder={placeholder}
            {...props}
          />

          {postfix && (
            <div className={cn([styles.textFieldPostfix, classNamePostfix])}>
              {postfix}
            </div>
          )}

          {success && (
            <span className={styles.textFieldSuccess}>
              <CheckRound />
            </span>
          )}
        </div>

        {error && typeof error !== "boolean" && (
          <span className={styles.textFieldError}>{error}</span>
        )}
      </div>
    );
  }
);

TextField.displayName = "TextField";

export default TextField;
